import { Injectable } from '@angular/core';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {classToPlain, plainToClass} from 'class-transformer';
import {db} from "../../dexie/db";

import { SOPutAttachment } from './so-put-attachment';
import { SOGetAttachment } from './so-get-attachment';
import { SOPostAttachment } from './so-post-attachment';
import { SODeleteAttachment } from './so-delete-attachment';
import {SORespAttachment} from './so-resp-attachment';
import {SOPluralAttachment} from './so-plural-attachment';

import {MMEntityService} from 'lionheartland';
import {EntityCoreProtocol} from 'lionheartland';
import {MMEntityServiceBase} from 'lionheartland';
import {HttpHeaders} from "@angular/common/http";

export class EOAttachmentService extends MMEntityServiceBase implements MMEntityService {

  constructor(
    public entityCore: EntityCoreProtocol,
  ) {
    super('attachment', entityCore);
  }

  /** GET Attachment by id. Will 404 if id not found */
  get(request: SOGetAttachment): Observable<SOPluralAttachment> {
    return this.httpClient.get<SOPluralAttachment>(this.makeGetUri(request), this.httpOptions).pipe(
      map(resp => plainToClass(SOPluralAttachment, resp)),
      tap(async (response: SOPluralAttachment) => {
        this.log(`fetched Attachment`);
        // After a successful server response, update IndexedDB
        const activities = response.toAttachments(); // Convert your response to an array of Attachment instances
        const promises = activities.map(attachment => db.attachments.put(attachment));
        await Promise.all(promises); // Wait for all the IndexedDB update operations to complete
      }),
      catchError(this.handleError<SOPluralAttachment>(`get Attachment`))
    );
  }

    /** GETCACHE for initial display with CacheQueryProtocol */
    getCache(cacheQuery: any): Observable<SOPluralAttachment> {
        console.log("GETCACHE", cacheQuery);

        if (cacheQuery.filterKey === 'attachment' && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on parent and organization")
            return from(db.attachments.where('parentUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(attachmentsArray => this.processAttachments(attachmentsArray)),
                catchError(error => this.handleQueryError(error))
            );
        }
        else if (cacheQuery.filterKey + "s" == "media" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.attachments.where('mediaUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(attachmentsArray => this.processAttachments(attachmentsArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "organization" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.attachments.where('organizationUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(attachmentsArray => this.processAttachments(attachmentsArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterOrganization || cacheQuery.organization.uid)
        {
            console.log("tripped organization for cache query")
            if ( cacheQuery?.filterOrganization != null )
            {
                console.log("cache filtering based on organization CQP", cacheQuery)
                return from(db.attachments.where('organizationUid').equals(cacheQuery.filterOrganization).toArray()).pipe(
                    map(attachmentsArray => this.processAttachments(attachmentsArray)),
                    catchError(error => this.handleQueryError(error))
                );
            }
            else if ( cacheQuery?.organization?.uid != null )
            {
                console.log("UID:", cacheQuery.organization.uid)
                console.log("cache filtering based on organization SOGet")
                return from(db.attachments.where('organizationUid').equals(cacheQuery.organization.uid).toArray()).pipe(
                    map(attachmentsArray => this.processAttachments(attachmentsArray)),
                    catchError(error => this.handleQueryError(error))
                );
            }
            else
            {
                console.log("null organization")
            }
        }
        else
        {
            console.log("cache no filter")
            return from(db.attachments.toArray()).pipe(
                map((attachmentsArray) => {
                    // Convert the array of plain objects to an array of SORespAttachment instances
                    const sorAttachments = attachmentsArray.map(req => plainToClass(SORespAttachment, req));
                    // Create and return a new instance of SOPluralAttachment
                    const pluralAttachment = new SOPluralAttachment();
                    pluralAttachment.objects = sorAttachments;
                    pluralAttachment.nextOffset = null; // Assuming there is no pagination in cache
                    return pluralAttachment;
                }),
                catchError(error => this.handleQueryError(error))
            );
        }
    }

    private processAttachments(attachmentsArray) {
        const sorAttachments = attachmentsArray.map(req => plainToClass(SORespAttachment, req));
        const pluralAttachment = new SOPluralAttachment();
        pluralAttachment.objects = sorAttachments;
        pluralAttachment.nextOffset = null;
        return pluralAttachment;
    }

    private handleQueryError(error) {
        console.error('Error in query:', error);
        return throwError(error); // Return an observable error
    }

post(request: SOPostAttachment): Observable<SORespAttachment> {
  return this.httpClient.post<SORespAttachment>(this.coreUrl + this.route, request, this.httpOptions).pipe(
    map(resp => plainToClass(SORespAttachment, resp)),

    tap(async (response: SORespAttachment) => {
      this.log(`added Attachment w/ id=${response.uid}`);
      // After a successful server response, update IndexedDB
      console.log("here", response);
      console.log("here request", request);
      await db.attachments.add(response.toAttachment());
    }),
    catchError(this.handleError<SORespAttachment>('Attachment post'))
  );
}


  put(request: SOPutAttachment): Observable<SORespAttachment> {
    const uid = request.uid;
    const uri = `${this.coreUrl}${this.route}/${uid}`;
    return this.httpClient.put<SORespAttachment>(uri, request, this.httpOptions).pipe(
      map(resp => plainToClass(SORespAttachment, resp)),
      tap(async (response: SORespAttachment) => {
        this.log(`edited Attachment w/ uid=${uid}`);
        // After a successful server response, update IndexedDB
        await db.attachments.put(response.toAttachment());
      }),
      catchError(this.handleError<SORespAttachment>('Attachment put'))
    );
  }


  delete(request: SODeleteAttachment): Observable<SORespAttachment> {
    return this.httpClient.delete<SORespAttachment>(this.makeDeleteUri(request), this.httpOptions).pipe(
      tap(async _ => {
        this.log(`deleted Attachment uid=${request.uid}`);
        // After a successful server response, update IndexedDB
        await db.attachments.delete(request.uid);
      }),
      catchError(this.handleError<SORespAttachment>('Attachment delete'))
    );
  }


}
